// src/pages/finance-portal/statements/StatementDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent, Chip,
    Alert, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const AGING_BUCKETS = [
    { key: 'current', label: 'Current', color: '#22c55e' },
    { key: 'days_1_30', label: '1\u201330 Days', color: '#eab308' },
    { key: 'days_31_60', label: '31\u201360 Days', color: '#f97316' },
    { key: 'days_61_90', label: '61\u201390 Days', color: '#ef4444' },
    { key: 'days_90_plus', label: '90+ Days', color: '#991b1b' },
];

export default function StatementDetail() {
    const navigate = useNavigate();
    const { statementId } = useParams();
    const [statement, setStatement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Send dialog
    const [sendOpen, setSendOpen] = useState(false);
    const [sending, setSending] = useState(false);

    const fetchStatement = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/statements/${statementId}`);
            if (res.data.success) {
                setStatement(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load statement');
        } finally {
            setLoading(false);
        }
    }, [statementId]);

    useEffect(() => { fetchStatement(); }, [fetchStatement]);

    const handleSend = async () => {
        setSending(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/statements/${statementId}/send`, {});
            setSendOpen(false);
            fetchStatement();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send statement');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (!statement) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>{error || 'Statement not found'}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance-portal/statements')} sx={{ fontFamily, textTransform: 'none', mt: 2 }}>
                    Back to Statements
                </Button>
            </Box>
        );
    }

    const aging = statement.aging || {};
    const agingTotal = AGING_BUCKETS.reduce((sum, b) => sum + (parseFloat(aging[b.key]) || 0), 0);

    const summaryCards = [
        { label: 'Opening Balance', value: statement.opening_balance, icon: <AccountBalanceIcon />, color: '#3b82f6' },
        { label: 'Invoiced', value: statement.total_invoiced, icon: <ReceiptLongIcon />, color: '#8b5cf6' },
        { label: 'Payments', value: statement.total_payments, icon: <PaymentsIcon />, color: '#22c55e' },
        { label: 'Credits', value: statement.total_credits, icon: <CreditScoreIcon />, color: '#f59e0b' },
        { label: 'Closing Balance', value: statement.closing_balance, icon: <TrendingDownIcon />, color: '#ef4444' },
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/finance-portal/statements')}
                            sx={{ fontFamily, textTransform: 'none', color: 'white', fontWeight: 600, mr: 0.5 }}
                        >
                            Back
                        </Button>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SummarizeIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Statement {statement.statement_number}
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                {statement.customer_name || statement.customer_company || 'Customer'} &bull; {formatDate(statement.period_start)} \u2013 {formatDate(statement.period_end)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        {statement.sent_at ? (
                            <Chip
                                label={`Sent ${formatDate(statement.sent_at)}`}
                                sx={{ fontFamily, fontWeight: 600, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.8rem' }}
                            />
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={<SendIcon />}
                                onClick={() => setSendOpen(true)}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: ACCENT,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Send Statement
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Summary Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
                    {summaryCards.map((card) => (
                        <Card key={card.label} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Box sx={{
                                        p: 0.75, borderRadius: '10px',
                                        bgcolor: `${card.color}15`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {React.cloneElement(card.icon, { sx: { fontSize: 20, color: card.color } })}
                                    </Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                                        {card.label}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 800, color: '#1e293b' }}>
                                    {formatCurrency(card.value)}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Aging Breakdown */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2.5 }}>Aging Breakdown</Typography>

                        {/* Stacked bar */}
                        <Box sx={{ display: 'flex', height: 32, borderRadius: '8px', overflow: 'hidden', mb: 2.5 }}>
                            {AGING_BUCKETS.map((bucket) => {
                                const amount = parseFloat(aging[bucket.key]) || 0;
                                const pct = agingTotal > 0 ? (amount / agingTotal) * 100 : 0;
                                if (pct <= 0) return null;
                                return (
                                    <Box
                                        key={bucket.key}
                                        sx={{
                                            width: `${pct}%`,
                                            bgcolor: bucket.color,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            minWidth: pct > 8 ? 'auto' : 0,
                                            transition: 'width 0.3s ease',
                                        }}
                                    >
                                        {pct > 10 && (
                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 700, color: 'white' }}>
                                                {Math.round(pct)}%
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                            {agingTotal <= 0 && (
                                <Box sx={{ width: '100%', bgcolor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>No aging data</Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Legend with amounts */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
                            {AGING_BUCKETS.map((bucket) => {
                                const amount = parseFloat(aging[bucket.key]) || 0;
                                return (
                                    <Box key={bucket.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: bucket.color, flexShrink: 0 }} />
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b' }}>{bucket.label}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(amount)}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </CardContent>
                </Card>

                {/* Line Items / Transactions */}
                {statement.line_items && statement.line_items.length > 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Transactions</Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily }}>
                                    <Box component="thead">
                                        <Box component="tr" sx={{ bgcolor: '#f8fafc' }}>
                                            {['Date', 'Type', 'Reference', 'Description', 'Amount', 'Balance'].map(h => (
                                                <Box component="th" key={h} sx={{
                                                    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                                                    textTransform: 'uppercase', textAlign: 'left', p: 1.5,
                                                    borderBottom: '2px solid #e2e8f0',
                                                }}>
                                                    {h}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box component="tbody">
                                        {statement.line_items.map((item, idx) => (
                                            <Box component="tr" key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' }, borderBottom: '1px solid #f1f5f9' }}>
                                                <Box component="td" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', p: 1.5 }}>
                                                    {formatDate(item.date)}
                                                </Box>
                                                <Box component="td" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', p: 1.5 }}>
                                                    <Chip
                                                        label={item.type || '\u2014'}
                                                        size="small"
                                                        sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20 }}
                                                    />
                                                </Box>
                                                <Box component="td" sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 600, p: 1.5 }}>
                                                    {item.reference || '\u2014'}
                                                </Box>
                                                <Box component="td" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', p: 1.5 }}>
                                                    {item.description || '\u2014'}
                                                </Box>
                                                <Box component="td" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: item.amount < 0 ? '#22c55e' : '#1e293b', p: 1.5 }}>
                                                    {formatCurrency(item.amount)}
                                                </Box>
                                                <Box component="td" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', p: 1.5 }}>
                                                    {formatCurrency(item.running_balance)}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Opening</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600 }}>{formatCurrency(statement.opening_balance)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>Closing</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(statement.closing_balance)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>

            {/* Send Confirmation Dialog */}
            <Dialog open={sendOpen} onClose={() => setSendOpen(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send Statement {statement.statement_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will send the statement to the customer via email.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSendOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
